import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { SUBSCRIPTION } from '@App/routes';
import Loader from '@App/components/ui/Loader';
import { useAppSelector } from '@App/store';
import { SoundEffectPropType, SoundEffectData } from '@App/components/model/SoundEffectPropType';

interface Props {
  mobile?: boolean
  sound: SoundEffectData
}

/** How long the button stays in its "downloaded" state before offering the download again. */
const FEEDBACK_DURATION = 2500;

/**
 * Reduced action bar for a sound effect row: direct download only (no side-panel).
 *
 * Without access to sound effects the button stays — the row must not look crippled — but it leads to
 * the subscription page instead of the file, mirroring the music row whose cart button sends an
 * unsubscribed visitor to the same place. The protected download route remains the source of truth
 * either way (T2.2 / T6.1); this is presentation only.
 *
 * The download itself is the browser's (`<a download>`), which reports nothing back: there is no
 * event for "the file has arrived". The acknowledgement is therefore local and optimistic — it says
 * "your click was taken", not "the file is on your disk" — and it reverts on its own so the same row
 * can be downloaded again. The click is never intercepted: preventing the default would trade a
 * missing animation for a missing file.
 */
export default function SoundEffectButtonBar({ sound }: Props) {
  const canAccessSoundEffects = useAppSelector(
    state => state.authentication?.permissions?.canAccessSoundEffects === true,
  );
  const [downloaded, setDownloaded] = useState<boolean>(false);
  // The subscription page renders server-side and takes about half a second warm, a couple of
  // seconds on a cold catalogue cache. The link is an ordinary navigation, so nothing on the page
  // moves in the meantime and the click reads as ignored.
  const [leaving, setLeaving] = useState<boolean>(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  // A row can be unmounted mid-feedback — the list re-renders on every new search — and a timer left
  // running would then set state on a component that no longer exists.
  useEffect(() => () => clearTimeout(timeout.current), []);

  // Coming back with the browser's back button restores the page from the bfcache with its React
  // state intact, spinner included — a button left spinning for ever on a page that has finished
  // loading. `pageshow` fires on that restore, where a plain unmount effect never runs.
  useEffect(() => {
    const onPageShow = () => setLeaving(false);

    window.addEventListener('pageshow', onPageShow);

    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const onDownload = () => {
    setDownloaded(true);
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => setDownloaded(false), FEEDBACK_DURATION);
  };

  if (!canAccessSoundEffects) {
    const label = I18n.t('model.soundEffect.actions.subscribeToDownload');

    return (
      <a
        className="action-button action-button--primary"
        href={SUBSCRIPTION.getUrl()}
        title={label}
        aria-label={label}
        aria-busy={leaving}
        // Not preventing the default: the navigation is the point, this only says it started.
        onClick={() => setLeaving(true)}
      >
        {leaving ? <Loader /> : (
          /* A euro sign rather than the download arrow: this button downloads nothing, it leads to
             the subscription page, and wearing the same icon as the one that does was promising a
             file the visitor cannot have.

             Inline SVG because the icon set is an Icomoon font with no euro glyph, and adding one
             means regenerating four font files. Sized in `em` and painted with `currentColor`, so it
             follows the button's size and its hover colour exactly like an `.icon` would.

             Material Symbols "euro_symbol" (Apache 2.0), the icon the client asked for. */
          <svg
            className="action-button__glyph"
            viewBox="0 -960 960 960"
            width="1em"
            height="1em"
            fill="currentColor"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M600-120q-118 0-210-67T260-360H120v-80h122q-2-11-2-20v-40q0-9 2-20H120v-80h140q38-106 130-173t210-67q69 0 130.5 24T840-748l-70 70q-35-29-78.5-45.5T600-740q-75 0-136.5 38.5T370-600h230v80H344q-2 11-3 20t-1 20q0 11 1 20t3 20h256v80H370q32 63 93.5 101.5T600-220q48 0 92.5-16.5T770-282l70 70q-48 44-109.5 68T600-120Z" />
          </svg>
        )}
      </a>
    );
  }

  const label = I18n.t(downloaded
    ? 'model.soundEffect.actions.downloaded'
    : 'model.soundEffect.actions.download');

  return (
    <a
      className={`action-button action-button--primary${downloaded ? ' action-button--done' : ''}`}
      href={sound.downloadUrl}
      title={label}
      aria-label={label}
      onClick={onDownload}
      download
    >
      <i className={`icon icon--${downloaded ? 'check' : 'download'}`} aria-hidden="true" />
    </a>
  );
}

SoundEffectButtonBar.propTypes = {
  mobile: PropTypes.bool,
  sound: SoundEffectPropType.isRequired,
};

SoundEffectButtonBar.defaultProps = {
  mobile: false,
};
